/**
 * OSRS-style XP curve.
 *
 * The exact closed-form formula is `(1/4) × Σ_{i=1..L-1} (i + 300 × 2^(i/7))`,
 * but the floating-point implementation drifts by ~12 XP across 120 levels
 * from the canonical Melvor/OSRS published table. We bake the canonical
 * table directly for accuracy (12 × 120 entries).
 *
 * Source: docs/CATALOGUE.md / Melvor Idle Experience Table wiki page.
 *
 * Verified:
 *   - xpForLevel(1)   = 0
 *   - xpForLevel(2)   = 83          (canonical OSRS)
 *   - xpForLevel(99)  = 13,034,431
 *   - xpForLevel(100) = 14,391,160
 *   - xpForLevel(120) = 104,273,167
 */

export const SKILL_MAX_LEVEL = 120;

/** Canonical OSRS XP table (cumulative XP at each level, 1-indexed). */
const XP_TABLE: ReadonlyArray<number> = [
  0,            // L=1
  83,           // L=2
  174,          // L=3
  276,          // L=4
  388,          // L=5
  512,          // L=6
  650,          // L=7
  801,          // L=8
  969,          // L=9
  1154,         // L=10
  1358,         // L=11
  1584,         // L=12
  1833,         // L=13
  2107,         // L=14
  2411,         // L=15
  2746,         // L=16
  3115,         // L=17
  3523,         // L=18
  3973,         // L=19
  4470,         // L=20
  5018,         // L=21
  5624,         // L=22
  6291,         // L=23
  7028,         // L=24
  7842,         // L=25
  8740,         // L=26
  9730,         // L=27
  10824,        // L=28
  12031,        // L=29
  13363,        // L=30
  14833,        // L=31
  16456,        // L=32
  18247,        // L=33
  20224,        // L=34
  22406,        // L=35
  24815,        // L=36
  27473,        // L=37
  30408,        // L=38
  33648,        // L=39
  37224,        // L=40
  41171,        // L=41
  45529,        // L=42
  50339,        // L=43
  55649,        // L=44
  61512,        // L=45
  67983,        // L=46
  75127,        // L=47
  83014,        // L=48
  91721,        // L=49
  101333,       // L=50
  111945,       // L=51
  123660,       // L=52
  136594,       // L=53
  150872,       // L=54
  166636,       // L=55
  184040,       // L=56
  203254,       // L=57
  224466,       // L=58
  247886,       // L=59
  273742,       // L=60
  302288,       // L=61
  333804,       // L=62
  368599,       // L=63
  407014,       // L=64
  449428,       // L=65
  496254,       // L=66
  547953,       // L=67
  605032,       // L=68
  668051,       // L=69
  737627,       // L=70
  814445,       // L=71
  899257,       // L=72
  992895,       // L=73
  1096278,      // L=74
  1210421,      // L=75
  1336443,      // L=76
  1475581,      // L=77
  1629200,      // L=78
  1798808,      // L=79
  1986068,      // L=80
  2192818,      // L=81
  2421087,      // L=82
  2673114,      // L=83
  2951373,      // L=84
  3258594,      // L=85
  3597792,      // L=86
  3972294,      // L=87
  4385776,      // L=88
  4842295,      // L=89
  5346332,      // L=90
  5902831,      // L=91
  6517253,      // L=92
  7195629,      // L=93
  7944614,      // L=94
  8771558,      // L=95
  9684577,      // L=96
  10692629,     // L=97
  11805606,     // L=98
  13034431,     // L=99
  14391160,     // L=100 (TotH threshold)
  15889109,     // L=101
  17542976,     // L=102
  19368992,     // L=103
  21385073,     // L=104
  23611006,     // L=105
  26068632,     // L=106
  28782069,     // L=107
  31777943,     // L=108
  35085654,     // L=109
  38737661,     // L=110
  42769801,     // L=111
  47221641,     // L=112
  52136869,     // L=113
  57563718,     // L=114
  63555443,     // L=115
  70170840,     // L=116
  77474828,     // L=117
  85539027,     // L=118
  94442737,     // L=119
  104273167,    // L=120
];

/** XP needed to advance from level (L-1) to level L. */
export function xpToNext(L: number): number {
  if (L < 1) throw new Error(`level must be >= 1, got ${L}`);
  if (L === 1) return 0;
  if (L > SKILL_MAX_LEVEL) return Infinity;
  return XP_TABLE[L - 1]! - XP_TABLE[L - 2]!;
}

/** Cumulative XP needed to BE at exactly level L (L=1 → 0 XP). */
export function xpForLevel(L: number): number {
  if (L <= 1) return 0;
  if (L > SKILL_MAX_LEVEL) return Infinity;
  return XP_TABLE[L - 1]!;
}

/** Highest level reached for a given cumulative XP. */
export function levelForXp(xp: number): number {
  if (xp <= 0) return 1;
  // Linear scan — table is 120 entries, faster than a binary search at this size.
  for (let L = 2; L <= SKILL_MAX_LEVEL; L += 1) {
    const threshold = XP_TABLE[L - 1]!;
    if (xp < threshold) return L - 1;
  }
  return SKILL_MAX_LEVEL;
}

/** XP gained per tick scales with current level (+4% per level). */
export function xpPerTick(baseXp: number, level: number): number {
  return Math.floor(baseXp * Math.pow(1.04, Math.max(0, level - 1)));
}